import React, { useState } from 'react';
import Popup from 'reactjs-popup';
import { Col, Container, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClone } from '@fortawesome/free-solid-svg-icons';
import Button from '../../Parts/Button';
import store, { type AppState, UsePortalDispatch } from '../../Store';
import { addQuoteAwning } from '../../Store/entities/awnings';
import { selectQuoteAwnings, useQuoteAwning } from '../../Store/entities/awnings/hooks';
import { getNextQuoteLineNumber } from '../../Store/entities/lineNumbers';
import { useQuoteTypeNameByQuoteId } from '../../Store/entities/quotes/hooks';
import { AWNING_PART_NUMBER } from '../awningTypes';
import { generateDuplicateShadeName } from '../awningConfiguratorUtils';

type AwningDuplicateProps = {
	quoteId: number;
	lineNumber: number;
};

const AwningDuplicate = ({ quoteId, lineNumber }: AwningDuplicateProps) => {
	const sourceAwning = useQuoteAwning(quoteId, lineNumber);
	const quoteType = useQuoteTypeNameByQuoteId(quoteId);
	const dispatch = UsePortalDispatch();
	const [isDuplicateOpen, setIsDuplicateOpen] = useState(false);
	const [duplicateCount, setDuplicateCount] = useState(1);
	const [isCurrentlyDuplicating, setIsCurrentlyDuplicating] = useState(false);

	if (!sourceAwning) return null;

	const displayName = sourceAwning.shade_name
		|| sourceAwning.name
		|| 'Awning';

	const onDuplicate = async () => {
		setIsCurrentlyDuplicating(true);
		try {
			for (let i = 0; i < duplicateCount; i += 1) {
				const state = store.getState() as AppState;
				const currentAwnings = selectQuoteAwnings(quoteId)(state);
				const existingNames = currentAwnings.map((awning) => awning.shade_name?.trim() ?? '');
				// Accessories and awnings share one line number sequence on the backend.
				const nextLineNumber = getNextQuoteLineNumber(quoteId, state);
				const shadeName = generateDuplicateShadeName(sourceAwning.shade_name ?? displayName, existingNames);

				await dispatch(addQuoteAwning({
					quoteId,
					sku: AWNING_PART_NUMBER,
					parms: sourceAwning.parameters ?? [],
					nextLineNumber,
					room_name: sourceAwning.room_name ?? '',
					shade_name: shadeName,
				})).unwrap();
			}
			setDuplicateCount(1);
			setIsDuplicateOpen(false);
		} finally {
			setIsCurrentlyDuplicating(false);
		}
	};

	return (
		<>
			<Popup
				open={isDuplicateOpen}
				onClose={() => setIsDuplicateOpen(false)}
				className="mobile-modal"
			>
				<div id="product-editor">
					<div className="product-form">
						<div style={{ overflowX: 'auto' }}>
							<h2 className={`text-${quoteType}`}>
								Duplicate Awning:
								{' '}
								{displayName}
							</h2>
							<Container className="local-bootstrap">
								<Row>
									<Col>
										How many awnings need to be made from this awning?
									</Col>
									<Col>
										<input
											value={duplicateCount}
											type="number"
											min={1}
											onChange={(e) => setDuplicateCount(Math.max(1, Number(e.target.value) || 1))}
										/>
									</Col>
								</Row>
							</Container>
							<Container className="local-bootstrap">
								<Row className="justify-content-center mt-2">
									<Col lg={4}>
										<Button
											color={quoteType ?? 'green'}
											fullWidth
											disabled={isCurrentlyDuplicating}
											onClick={() => setIsDuplicateOpen(false)}
										>
											{isCurrentlyDuplicating ? '...' : 'Cancel'}
										</Button>
									</Col>
									<Col lg={4}>
										<Button
											color={quoteType ?? 'green'}
											fullWidth
											disabled={isCurrentlyDuplicating}
											onClick={onDuplicate}
										>
											{isCurrentlyDuplicating ? 'Duplicating...' : 'Duplicate'}
										</Button>
									</Col>
								</Row>
							</Container>
						</div>
					</div>
				</div>
			</Popup>
			<Button
				fullWidth
				color="light"
				size="sm"
				title="Duplicate Awning"
				hoverColor={quoteType}
				onClick={() => setIsDuplicateOpen(true)}
			>
				<FontAwesomeIcon transform="grow-5" icon={faClone} />
			</Button>
		</>
	);
};

export default AwningDuplicate;
